import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';

import { MAINTENANCE_FAIL_MODE_PATH } from '../../../../config/paths';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';

import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import Content from '../../../layout/Content';

import { MAINTENANCE_WARNING_PRIORITY } from '../../../../config/constants';
import MaintenanceFailModeForm from './MaintenanceFailModeForm';
import {
  clearMaintenanceFailMode,
  getMaintenanceFailMode,
  putMaintenanceFailMode,
} from '../../../../actions';

const EditMaintenanceFailMode = ({
  breadcrumbs,
  maintenanceFailMode,
  match: {
    params: { id },
  },
  loading,
  dispatchClearMaintenanceFailMode,
  dispatchGetMaintenanceFailMode,
  dispatchPutMaintenanceFailMode,
}) => {
  useLayoutEffect(() => {
    dispatchGetMaintenanceFailMode({ maintenanceFailModeId: id });

    return () => dispatchClearMaintenanceFailMode();
  }, [dispatchGetMaintenanceFailMode, dispatchClearMaintenanceFailMode, id]);

  const onSubmit = (formValues) => {
    const maintenanceFailModeJS = maintenanceFailMode.toJS();

    const newFormValues = {
      ...formValues,
      id: Number(id),
    };

    newFormValues.categoryId = formValues.categoryId
      ? formValues.categoryId.value
      : null;

    newFormValues.subCategoryId = formValues.subCategoryId
      ? formValues.subCategoryId.value
      : null;

    newFormValues.priority = formValues.priority
      ? formValues.priority.value
      : null;

    newFormValues.activityId = formValues.activityId
      ? formValues.activityId.value
      : null;

    const formActivityIds = formValues.maintenanceFailModeActivityList || [];

    newFormValues.maintenanceFailModeActivityList = formActivityIds.map(
      (item) => {
        const existing =
          maintenanceFailModeJS.maintenanceFailModeActivityList.find(
            (old) => +old.activityId === +item.value,
          );

        return {
          ...(existing && existing.id ? { id: existing.id } : {}),
          maintenanceFailModeId: Number(id),
          activityId: item.value,
        };
      },
    );

    newFormValues.maintenanceFailModeCategoryList = (
      formValues.maintenanceFailModeCategoryList || []
    ).map((item) => ({
      ...(item.id ? { id: item.id } : {}),
      maintenanceFailModeId: Number(id),
      name: item.name,
    }));

    dispatchPutMaintenanceFailMode(id, newFormValues);
  };

  let content;

  if (loading) {
    content = <Loader />;
  } else if (maintenanceFailMode.isEmpty()) {
    content = <NoDataResource returnPage={MAINTENANCE_FAIL_MODE_PATH} />;
  } else {
    const maintenanceFailModeJS = maintenanceFailMode.toJS();

    const initialValues = {
      description: maintenanceFailModeJS.description,

      categoryId: maintenanceFailModeJS.category
        ? {
            value: maintenanceFailModeJS.categoryId,
            label: maintenanceFailModeJS.category.name,
          }
        : null,

      subCategoryId: maintenanceFailModeJS.subCategory
        ? {
            value: maintenanceFailModeJS.subCategoryId,
            label: maintenanceFailModeJS.subCategory.name,
          }
        : null,

      priority: maintenanceFailModeJS.priority
        ? {
            value: maintenanceFailModeJS.priority,
            label:
              MAINTENANCE_WARNING_PRIORITY[maintenanceFailModeJS.priority]
                .label,
          }
        : null,

      activityId: maintenanceFailModeJS.activity
        ? {
            value: maintenanceFailModeJS.activityId,
            label: maintenanceFailModeJS.activity.name,
          }
        : null,

      maintenanceFailModeActivityList:
        maintenanceFailModeJS.maintenanceFailModeActivityList.map(
          ({ activity, activityId }) => ({
            label: activity.name,
            value: activity.id || activityId,
          }),
        ),

      maintenanceFailModeCategoryList:
        maintenanceFailModeJS.maintenanceFailModeCategoryList,
    };

    content = (
      <MaintenanceFailModeForm
        onSubmit={onSubmit}
        initialValues={initialValues}
      />
    );
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Modo de Falla"
      subtitle="Editar Modo de Falla"
      content={content}
    />
  );
};

EditMaintenanceFailMode.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchGetMaintenanceFailMode: PropTypes.func.isRequired,
  dispatchClearMaintenanceFailMode: PropTypes.func.isRequired,
  dispatchPutMaintenanceFailMode: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  maintenanceFailMode: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditMaintenanceFailMode.defaultProps = {
  loading: false,
};

const mapStateToProps = (
  { MechanicalMaintenanceUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...MechanicalMaintenanceUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Modos de Falla',
      href: MAINTENANCE_FAIL_MODE_PATH,
    },
    {
      text: 'Ver',
      href: `${MAINTENANCE_FAIL_MODE_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],

  maintenanceFailMode: MechanicalMaintenanceUnit.MaintenanceFailMode.getIn([
    'current',
    'content',
  ]),

  loading: !MechanicalMaintenanceUnit.MaintenanceFailMode.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

const mapDispatchToProps = {
  dispatchGetMaintenanceFailMode: getMaintenanceFailMode,
  dispatchClearMaintenanceFailMode: clearMaintenanceFailMode,
  dispatchPutMaintenanceFailMode: putMaintenanceFailMode,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditMaintenanceFailMode);
